import React, { useEffect, useRef, useState } from 'react';
import { FaMapMarkerAlt, FaCheckCircle, FaTimesCircle, FaMap, FaInfoCircle } from 'react-icons/fa';

// Declare mapboxgl as a global
declare global {
  interface Window {
    mapboxgl: any;
  }
}

interface Property {
  id: number;
  address: string;
  city: string;
  state: string;
  zipcode: string;
  latitude: number | null;
  longitude: number | null;
  name?: string;
}

interface GeocodingResult {
  success: boolean;
  data?: {
    id: number;
    address: string;
    coordinates: { latitude: number; longitude: number };
    confidence: number;
    status: string;
  };
  error?: string;
}

interface GeocodingResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  results: GeocodingResult[];
  properties: Property[];
  companyName: string;
  totalDuration: string;
}

const GeocodingResultsModal: React.FC<GeocodingResultsModalProps> = ({
  isOpen,
  onClose,
  results,
  properties,
  companyName,
  totalDuration
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Debug logging
  console.log('GeocodingResultsModal props:', { results, properties, companyName, totalDuration });

  // Calculate statistics
  const successfulResults = results.filter(r => r.success);
  const failedResults = results.filter(r => !r.success);
  const successRate = results.length > 0 ? (successfulResults.length / results.length) * 100 : 0;

  console.log('Calculated stats:', { 
    totalResults: results.length, 
    successfulResults: successfulResults.length, 
    failedResults: failedResults.length, 
    successRate 
  });

  // Get properties with coordinates for mapping
  const propertiesWithCoordinates = properties.filter(p => p.latitude && p.longitude);

  useEffect(() => {
    if (!isOpen || !mapContainer.current || mapLoaded) return;

    // Load Mapbox GL JS
    const script = document.createElement('script');
    script.src = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.js';
    script.onload = () => {
      const link = document.createElement('link');
      link.href = 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);

      // Initialize map after CSS is loaded
      setTimeout(() => {
        initializeMap();
      }, 100);
    };
    document.head.appendChild(script);

    return () => {
      if (map.current) {
        map.current.remove();
      }
    };
  }, [isOpen, mapLoaded]);

  const initializeMap = () => {
    if (!mapContainer.current || !propertiesWithCoordinates.length) return;

    // Access mapboxgl from window
    const mapboxgl = window.mapboxgl;
    if (!mapboxgl) {
      console.error('Mapbox GL JS not loaded');
      return;
    }

    mapboxgl.accessToken = 'pk.eyJ1IjoiZXNlcnYiLCJhIjoiY2wzYnRxejN0MDBwYzNwcW1ydDUybmVpdSJ9.lgW57K_xQu7bwAn46nAdLQ';

    // Calculate bounds for all properties
    const bounds = new mapboxgl.LngLatBounds();
    propertiesWithCoordinates.forEach(prop => {
      if (prop.longitude && prop.latitude) {
        bounds.extend([prop.longitude, prop.latitude]);
      }
    });

    // Create map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      bounds: bounds,
      fitBoundsOptions: { padding: 50 }
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Add markers for each property
    propertiesWithCoordinates.forEach((prop, index) => {
      if (!prop.longitude || !prop.latitude) return;

      const result = results.find(r => r.data?.id === prop.id);
      const isSuccess = result?.success;
      
      // Create marker element
      const markerEl = document.createElement('div');
      markerEl.className = 'marker';
      markerEl.style.width = '24px';
      markerEl.style.height = '24px';
      markerEl.style.borderRadius = '50%';
      markerEl.style.border = '3px solid white';
      markerEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
      markerEl.style.cursor = 'pointer';
      markerEl.style.display = 'flex';
      markerEl.style.alignItems = 'center';
      markerEl.style.justifyContent = 'center';
      markerEl.style.color = 'white';
      markerEl.style.fontSize = '12px';
      markerEl.style.fontWeight = 'bold';

      // Color based on success/failure
      if (isSuccess) {
        markerEl.style.backgroundColor = '#10B981'; // Green for success
        markerEl.innerHTML = '✓';
      } else {
        markerEl.style.backgroundColor = '#EF4444'; // Red for failure
        markerEl.innerHTML = '✗';
      }

      // Create popup
      const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
        <div class="p-3">
          <h3 class="font-semibold text-gray-900">${prop.name || 'Property'}</h3>
          <p class="text-sm text-gray-600">${prop.address}</p>
          <p class="text-sm text-gray-600">${prop.city}, ${prop.state} ${prop.zipcode}</p>
          <p class="text-sm font-medium ${isSuccess ? 'text-green-600' : 'text-red-600'}">
            ${isSuccess ? 'Successfully Geocoded' : 'Geocoding Failed'}
          </p>
          ${isSuccess && result?.data ? `
            <p class="text-xs text-gray-500 mt-2">
              Confidence: ${(result.data.confidence * 100).toFixed(1)}%
            </p>
          ` : ''}
        </div>
      `);

      // Add marker to map
      new mapboxgl.Marker(markerEl)
        .setLngLat([prop.longitude, prop.latitude])
        .setPopup(popup)
        .addTo(map.current);
    });

    setMapLoaded(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-full">
                <FaMap size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Geocoding Complete!</h2>
                <p className="text-green-100">Results for {companyName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-green-100 transition-colors duration-200"
            >
              <FaTimesCircle size={24} />
            </button>
          </div>
        </div>

        {/* Content - Fixed height and proper flex layout */}
        <div className="flex flex-col lg:flex-row flex-1 min-h-0">
          {/* Left Side - Statistics - Made wider */}
          <div className="lg:w-2/5 p-6 border-r border-gray-200 overflow-y-auto">
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Summary</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{successfulResults.length}</div>
                    <div className="text-sm text-gray-600 mt-1">Successful</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-600">{failedResults.length}</div>
                    <div className="text-sm text-gray-600 mt-1">Failed</div>
                  </div>
                </div>
                <div className="mt-6 text-center">
                  <div className="text-xl font-semibold text-gray-900">
                    {successRate.toFixed(1)}% Success Rate
                  </div>
                  <div className="text-sm text-gray-500 mt-2">Duration: {totalDuration}</div>
                </div>
              </div>

              {/* Results List */}
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Property Results</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {results.map((result, index) => {
                    const property = properties.find(p => p.id === result.data?.id);
                    if (!property) return null;

                    return (
                      <div
                        key={index}
                        className={`p-4 rounded-lg border ${
                          result.success
                            ? 'border-green-200 bg-green-50'
                            : 'border-red-200 bg-red-50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`mt-1 ${result.success ? 'text-green-600' : 'text-red-600'}`}>
                            {result.success ? <FaCheckCircle size={18} /> : <FaTimesCircle size={18} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">
                              {property.name || `Property ${property.id}`}
                            </div>
                            <div className="text-sm text-gray-600 truncate">
                              {property.address}, {property.city}, {property.state} {property.zipcode}
                            </div>
                            {result.success && result.data && (
                              <div className="text-xs text-green-700 mt-2">
                                Confidence: {(result.data.confidence * 100).toFixed(1)}%
                              </div>
                            )}
                            {!result.success && (
                              <div className="text-xs text-red-700 mt-2">
                                Error: {result.error}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Map - Made narrower */}
          <div className="lg:w-3/5 p-6 flex flex-col">
            <div className="mb-4 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Property Locations</h3>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span>Successfully Geocoded</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span>Geocoding Failed</span>
                </div>
              </div>
            </div>
            
            {/* Map container with fixed height */}
            <div 
              ref={mapContainer} 
              className="flex-1 w-full rounded-lg border border-gray-200 overflow-hidden"
              style={{ minHeight: '400px', height: '100%' }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {propertiesWithCoordinates.length} properties displayed on map
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeocodingResultsModal;
